// Pricing utility functions for NGAP calculations
import { logger } from './logger';
import { REVALORIZATION_DATES, DATE_LABELS } from '../constants/dates';

export const POINT_VALUE_METROPOLE = 2.21; // €
export const POINT_VALUE_DROM = 2.43; // €

// Anciennes valeurs de point (avant revalorisation)
export const OLD_POINT_VALUE_METROPOLE = 2.15; // €
export const OLD_POINT_VALUE_DROM = 2.36; // €

// Indemnités de déplacement
export const DISPLACEMENT_ALLOWANCE = {
  IFS: 4.00, // € - Indemnité forfaitaire de déplacement pour les patients en situation de handicap
  NON_IFS: 2.50 // € - Indemnité de déplacement standard
} as const;

/**
 * Obtient la valeur numérique de l'indemnité de déplacement
 * @param indemnityType - Type d'indemnité ('IFS' ou autre)
 * @returns Valeur en euros de l'indemnité
 */
export function getDisplacementValue(indemnityType?: string): number {
  return indemnityType === 'IFS' ? DISPLACEMENT_ALLOWANCE.IFS : DISPLACEMENT_ALLOWANCE.NON_IFS;
}

// Retourne la valeur de cotation actuellement en vigueur pour un acte
export function getEffectiveActCotationValue(act: { cotation_valeur: string; revalo_july_2027?: string; cotation_valeur_july_2027?: string; revalo_sept_2026?: string; cotation_valeur_sept_2026?: string; revalo_may_2026?: string; cotation_valeur_may_2026?: string; revalo_janv_2026?: string; cotation_valeur_janv_2026?: string; }): string {
  const now = new Date();
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && todayStr >= '2027-07-01') {
    return act.cotation_valeur_july_2027;
  }
  if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && todayStr >= '2026-09-01') {
    return act.cotation_valeur_sept_2026;
  }
  if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && todayStr >= '2026-05-28') {
    return act.cotation_valeur_may_2026;
  }
  if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026 && todayStr >= '2026-01-01') {
    return act.cotation_valeur_janv_2026;
  }
  return act.cotation_valeur;
}

// Actes concernés par la revalorisation
export const REVALORIZED_ACTS = [
  'RAM', 'RAO', 'RSC', 'RSM', 'RIC', 'RIM', 
  'VIM', 'VIC', 'VSM', 'VSC', 'DRA', 'APM'
];

// Actes concernés par la revalorisation de mai 2026 (Avenant 8)
export const REVALORIZED_ACTS_2026 = [
  'VIC', 'TER', 'APM'
];

// Actes NMI concernés par la revalorisation de septembre 2026
export const REVALORIZED_NMI_ACTS = [
  'AMK', 'AMC'
];

// Calendrier de revalorisation
export const REVALORIZATION_SCHEDULE = [
  {
    date: REVALORIZATION_DATES.JANV_2026,
    additionalPoints: 0.6,
    label: DATE_LABELS.JANV_2026
  },
  {
    date: REVALORIZATION_DATES.MAY_2026,
    additionalPoints: 0.3,
    label: DATE_LABELS.MAY_2026,
    actsType: '2026'
  },
  {
    date: REVALORIZATION_DATES.SEPTEMBER_2026,
    additionalPoints: 1.0,
    label: DATE_LABELS.SEPTEMBER_2026,
    actsType: 'NMI'
  },
  {
    date: REVALORIZATION_DATES.JULY_2027,
    additionalPoints: 0.9, // 0.6 + 0.3 = 0.9 points au total
    label: DATE_LABELS.JULY_2027
  }
];

// Fonction pour vérifier si un acte est concerné par la revalorisation
export const isActRevalorized = (cotation: string, cotationValeur: string): boolean => {
  logger.debug('isActRevalorized called with:', { cotation, cotationValeur });
  
  // Vérifier si les valeurs sont vides ou nulles
  if (!cotation || !cotationValeur) {
    logger.debug('Empty values detected', { cotation, cotationValeur });
    return false;
  }
  
  // Extraire le code de l'acte (première partie avant l'espace)
  const actCode = cotation.split(' ')[0].trim();
  logger.debug('Extracted act code:', { actCode, originalCotation: cotation });
  
  // Cas spécial pour AMS 7,5
  if (actCode === 'AMS' && cotationValeur === '7,5') {
    logger.debug('Match found: AMS 7,5');
    return true;
  }
  
  // Cas spécial pour AMS 9,5 (revalorisation 2026)
  if (actCode === 'AMS' && cotationValeur === '9,5') {
    logger.debug('Match found: AMS 9,5');
    return true;
  }
  
  // Cas spécial pour AMK/AMC [10,11] (revalorisation NMI)
  if ((actCode === 'AMK' || actCode === 'AMC') && (cotationValeur === '10' || cotationValeur === '11')) {
    logger.debug('Match found:', { actCode, cotationValeur });
    return true;
  }
  
  // Autres actes concernés
  const isRevalorized = REVALORIZED_ACTS.includes(actCode) || 
         REVALORIZED_ACTS_2026.includes(actCode) || 
         REVALORIZED_NMI_ACTS.includes(actCode);
  
  logger.debug('Revalorization check result:', { actCode, isRevalorized });
  
  return isRevalorized;
};

// Fonction pour calculer les points supplémentaires selon la date
export const getAdditionalPoints = (calculationDate: Date = new Date(), cotation?: string, cotationValeur?: string): number => {
  const sortedSchedule = REVALORIZATION_SCHEDULE.sort((a, b) => b.date.getTime() - a.date.getTime());
  
  // Extraire le code de l'acte si cotation est fournie
  const actCode = cotation ? cotation.split(' ')[0].trim() : '';
  
  let totalPoints = 0;
  
  for (const schedule of sortedSchedule) {
    if (calculationDate >= schedule.date) {
      // Vérifier le type d'acte pour les revalorisations spécifiques
      if (schedule.actsType === '2026') {
        // Revalorisation mai 2026 (Avenant 8) : VIC, TER, APM (AMS 9,5)
        if (actCode && (REVALORIZED_ACTS_2026.includes(actCode) || 
            (actCode === 'AMS' && cotationValeur === '9,5'))) {
          totalPoints += schedule.additionalPoints;
        }
      } else if (schedule.actsType === 'NMI') {
        // Revalorisation septembre 2026 : AMK/AMC [10,11]
        if (actCode && REVALORIZED_NMI_ACTS.includes(actCode) && 
            cotationValeur && (cotationValeur === '10' || cotationValeur === '11')) {
          totalPoints += schedule.additionalPoints;
        }
      } else {
        // Revalorisation générale (juillet 2025 et 2027)
        if (actCode && (REVALORIZED_ACTS.includes(actCode) || 
            (actCode === 'AMS' && cotationValeur === '7,5'))) {
          totalPoints += schedule.additionalPoints;
        }
      }
    }
  }
  
  return totalPoints;
};

// Fonction pour calculer le tarif selon la zone et la date
export const calculateTarif = (
  cotationValue: string, 
  isDrom: boolean = false, 
  cotation?: string,
  calculationDate: Date = new Date(),
  customPointValues?: { metropole: number; drom: number }
): number => {
  const numericValue = parseFloat(cotationValue.replace(',', '.'));
  if (isNaN(numericValue)) return 0;
  
  let finalValue = numericValue;
  
  // Ajouter les points de revalorisation si l'acte est concerné
  if (cotation && isActRevalorized(cotation, cotationValue)) {
    const additionalPoints = getAdditionalPoints(calculationDate, cotation, cotationValue);
    finalValue += additionalPoints;
  }
  
  const pointValue = isDrom ? 
    (customPointValues?.drom || POINT_VALUE_DROM) : 
    (customPointValues?.metropole || POINT_VALUE_METROPOLE);
    
  return finalValue * pointValue;
};

// Fonction pour formater le tarif
export const formatTarif = (tarif: number): string => {
  return (Math.round(tarif * 100) / 100).toFixed(2);
};

// Fonction pour obtenir tous les tarifs d'évolution pour un acte
export const getTariffEvolution = (act: NgapAct, isDrom: boolean = false, customPointValues?: { metropole: number; drom: number }) => {
  const pointValue = isDrom ? 
    (customPointValues?.drom || POINT_VALUE_DROM) : 
    (customPointValues?.metropole || POINT_VALUE_METROPOLE);
    
  const now = new Date();
  // Comparaison par date locale (YYYY-MM-DD) pour éviter le décalage UTC
  const todayStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  const janv2026 = new Date('2026-01-01');
  const may2026 = new Date('2026-05-28');
  const september2026 = new Date('2026-09-01');
  const july2027 = new Date('2027-07-01');

  const isOnOrAfter = (dateStr: string) => todayStr >= dateStr;

  // Déterminer la valeur de cotation actuellement en vigueur
  let currentActiveCotationValue = act.cotation_valeur;

  if (act.revalo_july_2027 && act.cotation_valeur_july_2027 && isOnOrAfter('2027-07-01')) {
    currentActiveCotationValue = act.cotation_valeur_july_2027;
  } else if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026 && isOnOrAfter('2026-09-01')) {
    currentActiveCotationValue = act.cotation_valeur_sept_2026;
  } else if (act.revalo_may_2026 && act.cotation_valeur_may_2026 && isOnOrAfter('2026-05-28')) {
    currentActiveCotationValue = act.cotation_valeur_may_2026;
  } else if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026 && isOnOrAfter('2026-01-01')) {
    currentActiveCotationValue = act.cotation_valeur_janv_2026;
  }
  
  const evolution: any = {
    current: {
      value: parseFloat(currentActiveCotationValue.replace(',', '.')) * pointValue,
      label: DATE_LABELS.CURRENT,
      date: now
    },
  };
  
  // Ajouter janvier 2026 si l'acte a une revalorisation prévue
  if (act.revalo_janv_2026 && act.cotation_valeur_janv_2026) {
    const janv2026Value = parseFloat(act.cotation_valeur_janv_2026.replace(',', '.'));
    if (!isNaN(janv2026Value) && janv2026Value > 0) {
      evolution.janv2026 = {
        value: janv2026Value * pointValue,
        label: DATE_LABELS.JANV_2026,
        date: janv2026
      };
    }
  }
  
  // Ajouter mai 2026 si l'acte est concerné (Avenant 8)
  if (act.revalo_may_2026 && act.cotation_valeur_may_2026) {
    const may2026Value = parseFloat(act.cotation_valeur_may_2026.replace(',', '.'));
    if (!isNaN(may2026Value) && may2026Value > 0) {
      evolution.may2026 = {
        value: may2026Value * pointValue,
        label: DATE_LABELS.MAY_2026,
        date: may2026
      };
    }
  }
  
  // Ajouter septembre 2026 si l'acte NMI est concerné
  if (act.revalo_sept_2026 && act.cotation_valeur_sept_2026) {
    const sept2026Value = parseFloat(act.cotation_valeur_sept_2026.replace(',', '.'));
    if (!isNaN(sept2026Value) && sept2026Value > 0) {
      evolution.september2026 = {
        value: sept2026Value * pointValue,
        label: DATE_LABELS.SEPTEMBER_2026,
        date: september2026
      };
    }
  }
  
  // Ajouter juillet 2027 pour les actes de la première revalorisation
  if (act.revalo_july_2027 && act.cotation_valeur_july_2027) {
    const july2027Value = parseFloat(act.cotation_valeur_july_2027.replace(',', '.'));
    if (!isNaN(july2027Value) && july2027Value > 0) {
      evolution.july2027 = {
        value: july2027Value * pointValue,
        label: DATE_LABELS.JULY_2027,
        date: july2027
      };
    }
  }
  
  return evolution;
};